/**
 * Static preset content for the v2 wizard, shipped as typed, tested data so the
 * UI and the tests share one source of truth. Nothing here needs a model:
 *  - `TOPIC_PRESETS`: 60 topics across 6 verticals; the Focus step samples 10.
 *  - `POPULAR_NEWSLETTERS`: 20 real, evergreen newsletters; step 6 samples 5.
 *
 * Sampling is deterministic: seeded from the draft id (and a reshuffle salt) so a
 * refresh does not reshuffle, but "show different ones" does. Balanced sampling
 * draws across verticals rather than clumping into one.
 */

/** A tappable focus topic. `vertical` keeps sampling balanced. */
export interface TopicPreset {
  readonly id: string;
  readonly label: string;
  readonly vertical: string;
}

/** A well-known newsletter shown as inspiration on step 6. */
export interface NewsletterPreset {
  readonly id: string;
  readonly name: string;
  readonly url: string;
  readonly host: string;
  readonly blurb: string;
  readonly vertical: string;
}

function topics(vertical: string, labels: readonly string[]): TopicPreset[] {
  return labels.map((label) => ({
    id: label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, ''),
    label,
    vertical,
  }));
}

/** 60 topics, 6 verticals of 10. Keeps the shipped v1 examples in the list. */
export const TOPIC_PRESETS: readonly TopicPreset[] = [
  ...topics('Tech and software', [
    'AWS security',
    'Cloud infrastructure',
    'Cybersecurity',
    'AI and machine learning',
    'Web development',
    'DevOps and platform engineering',
    'Open source',
    'Data engineering',
    'Developer tools',
    'Startups and founders',
  ]),
  ...topics('Business and finance', [
    'Personal finance',
    'Investing and markets',
    'Venture capital',
    'Marketing and growth',
    'SaaS and B2B',
    'E-commerce',
    'Real estate',
    'Crypto and web3',
    'Careers and work',
    'Small business',
  ]),
  ...topics('Creative and culture', [
    'Film and TV',
    'Music',
    'Books and reading',
    'Photography',
    'Graphic design',
    'Writing and journalism',
    'Video games',
    'Indie game dev',
    'Art and illustration',
    'Pop culture',
  ]),
  ...topics('Lifestyle and wellness', [
    'Fitness and training',
    'Nutrition and cooking',
    'Mental health',
    'Productivity',
    'Travel',
    'Parenting',
    'Fashion and style',
    'Home and interiors',
    'Coffee and tea',
    'Minimalism',
  ]),
  ...topics('Science and learning', [
    'Space and astronomy',
    'Climate and sustainability',
    'Health and biotech',
    'Psychology',
    'History',
    'Education',
    'Physics',
    'Neuroscience',
    'Nature and wildlife',
    'Mathematics',
  ]),
  ...topics('Local and community', [
    'Local food scene',
    'Local news',
    'City events',
    'Sports',
    'Outdoors and hiking',
    'Cars and motoring',
    'Pets',
    'DIY and making',
    'Gardening',
    'Volunteering',
  ]),
];

/** 20 real, evergreen newsletters. No metrics, stable brands. */
export const POPULAR_NEWSLETTERS: readonly NewsletterPreset[] = [
  {
    id: 'morning-brew',
    name: 'Morning Brew',
    url: 'https://www.morningbrew.com',
    host: 'morningbrew.com',
    blurb: 'Business news with a light, witty morning tone.',
    vertical: 'Business',
  },
  {
    id: 'the-hustle',
    name: 'The Hustle',
    url: 'https://thehustle.co',
    host: 'thehustle.co',
    blurb: 'Startup and tech stories told like a friend texting you.',
    vertical: 'Business',
  },
  {
    id: '1440',
    name: '1440',
    url: 'https://join1440.com',
    host: 'join1440.com',
    blurb: 'A no-spin daily brief across news, science and culture.',
    vertical: 'News',
  },
  {
    id: 'axios-am',
    name: 'Axios AM',
    url: 'https://www.axios.com/newsletters',
    host: 'axios.com',
    blurb: 'Smart-brevity morning news in scannable bullets.',
    vertical: 'News',
  },
  {
    id: 'tldr',
    name: 'TLDR',
    url: 'https://tldr.tech',
    host: 'tldr.tech',
    blurb: 'A five-minute daily digest of tech and startups.',
    vertical: 'Tech',
  },
  {
    id: 'the-rundown-ai',
    name: 'The Rundown AI',
    url: 'https://www.therundown.ai',
    host: 'therundown.ai',
    blurb: 'Daily plain-language roundup of what is new in AI.',
    vertical: 'AI',
  },
  {
    id: 'ahead-of-ai',
    name: 'Ahead of AI',
    url: 'https://magazine.sebastianraschka.com',
    host: 'magazine.sebastianraschka.com',
    blurb: 'Deeper reads on machine learning and research.',
    vertical: 'AI',
  },
  {
    id: 'lennys-newsletter',
    name: "Lenny's Newsletter",
    url: 'https://www.lennysnewsletter.com',
    host: 'lennysnewsletter.com',
    blurb: 'Product, growth and career advice for builders.',
    vertical: 'Product',
  },
  {
    id: 'stratechery',
    name: 'Stratechery',
    url: 'https://stratechery.com',
    host: 'stratechery.com',
    blurb: 'Strategy analysis of tech and media businesses.',
    vertical: 'Tech',
  },
  {
    id: 'platformer',
    name: 'Platformer',
    url: 'https://www.platformer.news',
    host: 'platformer.news',
    blurb: 'Reporting on the intersection of tech and democracy.',
    vertical: 'Tech',
  },
  {
    id: 'big-technology',
    name: 'Big Technology',
    url: 'https://www.bigtechnology.com',
    host: 'bigtechnology.com',
    blurb: 'Level-headed takes on big tech, minus the hype.',
    vertical: 'Tech',
  },
  {
    id: 'money-stuff',
    name: 'Money Stuff',
    url: 'https://www.bloomberg.com/account/newsletters/money-stuff',
    host: 'bloomberg.com',
    blurb: "Matt Levine's wry column on finance and markets.",
    vertical: 'Finance',
  },
  {
    id: 'not-boring',
    name: 'Not Boring',
    url: 'https://www.notboring.co',
    host: 'notboring.co',
    blurb: 'Business strategy essays that are, well, not boring.',
    vertical: 'Business',
  },
  {
    id: 'the-generalist',
    name: 'The Generalist',
    url: 'https://www.generalist.com',
    host: 'generalist.com',
    blurb: 'Long-form pieces on startups, VC and the people behind them.',
    vertical: 'Startups',
  },
  {
    id: 'milk-road',
    name: 'Milk Road',
    url: 'https://milkroad.com',
    host: 'milkroad.com',
    blurb: 'Crypto news with jokes, for people who hate crypto-bro energy.',
    vertical: 'Crypto',
  },
  {
    id: 'dense-discovery',
    name: 'Dense Discovery',
    url: 'https://www.densediscovery.com',
    host: 'densediscovery.com',
    blurb: 'A weekly find of design, tech and thoughtful tools.',
    vertical: 'Design',
  },
  {
    id: 'the-marginalian',
    name: 'The Marginalian',
    url: 'https://www.themarginalian.org',
    host: 'themarginalian.org',
    blurb: 'Maria Popova on books, art and the examined life.',
    vertical: 'Culture',
  },
  {
    id: 'letters-from-an-american',
    name: 'Letters from an American',
    url: 'https://heathercoxrichardson.substack.com',
    host: 'heathercoxrichardson.substack.com',
    blurb: "Daily context on US politics through a historian's lens.",
    vertical: 'Politics',
  },
  {
    id: 'noahpinion',
    name: 'Noahpinion',
    url: 'https://www.noahpinion.blog',
    host: 'noahpinion.blog',
    blurb: 'Noah Smith on economics, policy and the future.',
    vertical: 'Economics',
  },
  {
    id: 'garbage-day',
    name: 'Garbage Day',
    url: 'https://www.garbageday.email',
    host: 'garbageday.email',
    blurb: 'What is actually happening on the internet this week.',
    vertical: 'Internet culture',
  },
];

/* ------------------------------------------------------- seeded sampling -- */

/** Small, stable string hash (FNV-1a). */
export function hashSeed(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

/** mulberry32 PRNG: deterministic floats in [0, 1) from a numeric seed. */
function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Deterministic Fisher-Yates shuffle driven by a seeded PRNG. */
export function seededShuffle<T>(items: readonly T[], seed: number): T[] {
  const out = [...items];
  const random = mulberry32(seed);
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    const a = out[i]!;
    out[i] = out[j]!;
    out[j] = a;
  }
  return out;
}

/**
 * Sample `count` topics, balanced across verticals. Each vertical is shuffled
 * deterministically from the seed, then we draw round-robin so the result spans
 * several verticals rather than clumping into one.
 */
export function sampleTopics(seedKey: string, count = 10): TopicPreset[] {
  const seed = hashSeed(seedKey);
  const byVertical = new Map<string, TopicPreset[]>();
  for (const topic of TOPIC_PRESETS) {
    const bucket = byVertical.get(topic.vertical) ?? [];
    bucket.push(topic);
    byVertical.set(topic.vertical, bucket);
  }
  const queues = [...byVertical.values()].map((bucket, index) =>
    seededShuffle(bucket, seed + index * 101),
  );
  const orderedVerticals = seededShuffle(queues, seed);
  const picked: TopicPreset[] = [];
  let progressed = true;
  while (picked.length < count && progressed) {
    progressed = false;
    for (const queue of orderedVerticals) {
      if (picked.length >= count) {
        break;
      }
      const next = queue.shift();
      if (next) {
        picked.push(next);
        progressed = true;
      }
    }
  }
  return picked;
}

/** Sample `count` popular newsletters, deterministically from the seed. */
export function sampleNewsletters(seedKey: string, count = 5): NewsletterPreset[] {
  return seededShuffle(POPULAR_NEWSLETTERS, hashSeed(seedKey)).slice(0, count);
}
